/**
 * lib/holidays.ts
 * ------------------------------------------------------------------
 * Botswana public holidays — hardcoded for the demo. Used by the
 * HR clocking calendar to block out non-working days, and by the
 * seed script to skip these dates when generating punches.
 *
 * For the scoped production build this should move to a Firestore
 * collection so the school can edit it without a redeploy. The
 * shape `{ date: 'YYYY-MM-DD', name: string }` is the same either
 * way, so the screens won't need to change.
 *
 * Sources: Botswana Public Holidays Act + 2026 gazette dates.
 * Easter-derived dates (Good Friday, Easter Monday, Ascension)
 * computed from 2026 Easter = 5 April.
 * ------------------------------------------------------------------ */

export interface Holiday {
  /** ISO date, YYYY-MM-DD */
  date: string;
  name: string;
}

export const HOLIDAYS_2026: Holiday[] = [
  { date: '2026-01-01', name: "New Year's Day" },
  { date: '2026-01-02', name: 'Public Holiday' },
  { date: '2026-04-03', name: 'Good Friday' },
  { date: '2026-04-04', name: 'Holy Saturday' },
  { date: '2026-04-06', name: 'Easter Monday' },
  { date: '2026-05-01', name: 'Labour Day' },
  { date: '2026-05-14', name: 'Ascension Day' },
  { date: '2026-07-01', name: "Sir Seretse Khama Day" },
  { date: '2026-07-20', name: "President's Day" },
  { date: '2026-07-21', name: "President's Day Holiday" },
  { date: '2026-09-30', name: 'Botswana Day' },
  { date: '2026-10-01', name: 'Botswana Day Holiday' },
  { date: '2026-12-25', name: 'Christmas Day' },
  { date: '2026-12-26', name: 'Boxing Day' },
];

/** Lookup map: date -> holiday name. */
export const HOLIDAY_BY_DATE: Map<string, string> = new Map(
  HOLIDAYS_2026.map((h) => [h.date, h.name]),
);

/** Is the given YYYY-MM-DD a Botswana public holiday? */
export function isHoliday(dateStr: string): boolean {
  return HOLIDAY_BY_DATE.has(dateStr);
}

/** Name of the holiday on that date, or undefined. */
export function holidayName(dateStr: string): string | undefined {
  return HOLIDAY_BY_DATE.get(dateStr);
}

/** Is the given JS Date a Saturday or Sunday? */
export function isWeekend(date: Date): boolean {
  const d = date.getDay();
  return d === 0 || d === 6;
}

/**
 * Working day = not weekend and not a public holiday. The school's
 * own discretionary closure days (in-service, term breaks) are NOT
 * modelled here yet — those will come from the academic terms
 * Firestore data in the scoped build.
 */
export function isWorkingDay(date: Date, dateStr?: string): boolean {
  if (isWeekend(date)) return false;
  const key = dateStr ?? date.toISOString().slice(0, 10);
  return !isHoliday(key);
}
